import { useJSONFetcher } from "@outplayed/json-fetcher";
import { TooltipContainer } from "@outplayed/tooltips";
import Icon from "components/Common/Icon";
import { useQueryString } from "hooks/general-hooks";
import { DateTime } from "luxon";
import React, { useEffect, useMemo, useState } from "react";
import { ReactComponent as TooltipIcon } from "svg/question-line.svg";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import { getEncounterId, useWowPath } from "../../../hooks/path-hooks";
import {
  EXPANSION,
  PATCH,
  getAbbreviatedClass,
  getAbbreviatedSpec,
  getDifficultyPath,
  getFormattedEncounterType,
  getFormattedNames,
  getIcyVeinsLinkName,
  getIcyVeinsRole,
  getIsHealer,
  getIsTank,
  getMainStatData,
  getPrimaryStat,
  isClassHunter,
} from "../../../lib/wowHelpers";
import { validParams } from "../../../query_parameters/valid_parameters";
import { WoWContext } from "../../App";
import DataDetails from "../../Common/DataDetails";
import NoData from "../../Common/NoData";
import OctagonTalentIcon from "../../Common/OctagonTalentIcon";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";
import PvEGearGuide from "../../GearGuide/PvEGearGuide";
import Layout from "../Layout";
import { tierListColor } from "../TierLists/TierListHelper";
import BuildTabs from "./BuildTabs";
import StatPriorities from "./StatPriorities";
import WOWTalentTreeSection from "./TalentTreeSection";
import TierSetContainer from "./TierSetContainer";

const WOWBuildsContainer = (props) => {
  const { classType, specType, encounterType, optionalParams } = useWowPath();
  const [selectedBuild, setSelectedBuild] = useState(encounterType === "raid" ? "single_target" : "all_affixes");
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);
  const encounterID = getEncounterId(optionalParams, encounterType);
  const { difficulty, affix } = useQueryString();
  const difficultyPath = getDifficultyPath(difficulty, encounterType);

  const { data: builds, loading: buildsIsLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/${difficultyPath}/${dataClassName}/${dataClassName}_builds.json`,
    { ssr: true },
  );

  const specs = validParams.builds.specs[classType];
  const allTrees = {};
  for (let i = 0; i < 4; i++) {
    const { data: specData } = useJSONFetcher(`https://static.bigbrain.gg/wow/tree/v9/${classType}_${specs?.[i]?.value}.json`, {
      ssr: true,
    });
    allTrees[specs?.[i]?.value] = specData;
  }

  const classAndSpecTree = allTrees[specType];

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setSelectedBuild(encounterType === "raid" ? "single_target" : "all_affixes");
  }, [encounterType]);

  // TODO: Remove augmentation check once we have data for that spec
  if (buildsIsLoading || (!classAndSpecTree && specType !== "augmentation")) {
    return null;
  }

  const classSpec = `${dataClassName}-${dataSpecName}`;
  const hasData =
    builds &&
    (affix && affix !== "all"
      ? (builds.affixes?.[affix]?.[encounterID]?.[classSpec]?.stats?.total ?? 0) > 0
      : (builds[encounterType]?.[encounterID]?.[classSpec]?.stats?.total ?? 0) > 0);

  const buildsData =
    hasData &&
    (affix && affix !== "all" ? builds.affixes[affix][encounterID][classSpec] : builds[encounterType][encounterID][classSpec]);

  // If we're using default filters for raid or mythic+, then we want to use the selected multi-build
  let selectedTalentsData = buildsData;
  if (encounterType === "raid" && encounterID === "all") {
    selectedTalentsData = builds ? builds[selectedBuild]?.all[classSpec] : [];
  } else if (encounterType === "mythic" && encounterID === "all" && (!affix || affix === "all")) {
    if (selectedBuild === "all_affixes") {
      selectedTalentsData = builds ? builds.mythic.all[classSpec] : [];
    } else if (selectedBuild === "tyrannical" || selectedBuild === "fortified") {
      selectedTalentsData = builds ? builds.affixes[selectedBuild].all[classSpec] : [];
    }
  }

  // Show multi-builds for default filters for raid & mythic+, but hide for healers
  let showBuildTabs = false;
  if (getIsHealer(specType)) {
    showBuildTabs = false;
  } else if (encounterType === "raid" && encounterID === "all") {
    showBuildTabs = true;
  } else if (encounterType === "mythic" && encounterID === "all" && (!affix || affix === "all")) {
    showBuildTabs = true;
  }

  const { mainStat, statLabel, statTooltip } = getMainStatData(
    encounterType,
    specType,
    selectedTalentsData?.talents?.max_key,
    selectedTalentsData?.talents?.hps,
    selectedTalentsData?.talents?.dps,
  );

  const topSectionData = {
    encounterID: encounterID,
    encounterType: encounterType,
    dataClassName: dataClassName,
    dataSpecName: dataSpecName,
    primaryStats: builds ? buildsData?.stat_priorities?.primary_stats : {},
    stat_priorities: builds ? buildsData?.stat_priorities?.stat_priorities?.split("-") : [],
    pickrate: builds ? buildsData?.stats?.pickrate : 0,
    dps: builds ? buildsData?.stats?.dps : 0,
    hps: builds ? buildsData?.stats?.hps : 0,
    specType: specType,
    builds: builds,
    parses: builds ? buildsData?.stats?.count : 0,
  };

  const talentTreeSectionData = {
    classType,
    encounterType,
    mainStat,
    popularity: builds ? parseFloat(selectedTalentsData?.talents?.perc).toFixed(1) + "%" : "0%",
    selectedTalents: selectedTalentsData?.talents?.talent.split("-") ?? [],
    classAndSpecTree,
    specType,
    statLabel,
    statTooltip,
    allTrees,
    showViewMore: true,
  };

  const talentTreeContext = {
    allTrees,
    selectedTalents: selectedTalentsData?.talents?.talent.split("-") ?? [],
    specType,
  };

  const pageDescription = `${displaySpecName} ${displayClassName} talents with U.GG's best data for ${getFormattedEncounterType(
    encounterType,
  )}. The best ${getAbbreviatedSpec(displaySpecName)} ${getAbbreviatedClass(
    displayClassName,
  )} talents, from top gear to stat priorities, for ${EXPANSION} ${PATCH}.`;

  return (
    <Layout
      className="wow-builds-container"
      classType={classType}
      filterPage="builds"
      metaTitle={`${displaySpecName} ${displayClassName} Talents, Stat Priority, and Top Gear - ${EXPANSION} ${PATCH}`}
      pageDescription={pageDescription}
      pageTitle={`Talents, Stats, and Gear for ${getFormattedEncounterType(encounterType)}`}
      specType={specType}
    >
      {buildsData?.stats?.count && builds?.timestamp && (
        <DataDetails
          encounterType={encounterType}
          numLogsTitle="Total Parses"
          timestamp={DateTime.fromISO(builds.timestamp).toRelative()}
          parses={buildsData.stats.count}
          maxMythicKey={builds ? buildsData.stats.max_mythic_keys : 0}
          minMythicKey={builds ? buildsData.stats.min_mythic_keys : 0}
          useDetailedTitle
        />
      )}
      {hasData ? (
        <WoWContext.Provider value={talentTreeContext}>
          <div className="wow-recommended-build">
            <WOWBuildsStatSection {...topSectionData} />
            {showBuildTabs ? (
              <BuildTabs activeTab={selectedBuild} onClick={setSelectedBuild} encounterType={encounterType} />
            ) : null}
            {builds ? <WOWTalentTreeSection {...talentTreeSectionData} /> : null}
            {builds ? (
              <PvEGearGuide
                builds={buildsData.items}
                combos={buildsData.combos}
                classType={classType}
                specType={specType}
                encounterType={encounterType}
              />
            ) : null}
            <TierSetContainer className={displayClassName} specName={displaySpecName} encounterType={encounterType} />
            <PageSectionCrossLinks
              classType={classType}
              description={`Our friends at Icy Veins have you covered, with in-depth guides on rotations, talent trees, stat priorities, and gear for ${displaySpecName} ${displayClassName}.`}
              guideData={[
                {
                  description: `Explore a streamlined guide for the essentials of ${displaySpecName} ${displayClassName}`,
                  link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                    classType,
                  )}-pve-${getIcyVeinsRole(specType)}-easy-mode`,
                  title: "Easy Mode Guide",
                },
                {
                  description: `Unlock the optimal builds and talents for ${displaySpecName} ${displayClassName}`,
                  link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                    classType,
                  )}-pve-${getIcyVeinsRole(specType)}-spec-builds${isClassHunter(classType)}-talents`,
                  title: "Builds and Talents Guide",
                },
                {
                  description: `Discover the key stats to elevate your ${displaySpecName} ${displayClassName}`,
                  link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                    classType,
                  )}-pve-${getIcyVeinsRole(specType)}-stat-priority`,
                  title: "Stat Priority Guide",
                },
              ]}
              specType={specType}
            />
          </div>
        </WoWContext.Provider>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

const WOWBuildsStatSection = (props) => {
  const {
    hps,
    dps,
    pickrate,
    encounterID,
    encounterType,
    dataClassName,
    dataSpecName,
    classType,
    specType,
    builds,
    stat_priorities: priorities,
    primaryStats,
    parses,
  } = props;

  const modifiedPickrate = (Math.round(pickrate * 10000) / 100).toFixed(1);
  const statPerSecond = getIsHealer(specType) ? hps : dps;
  const statPerSecondLabel = getIsHealer(specType) ? "HPS" : getIsTank(specType) ? "Tank DPS" : "DPS";
  const statPerSecondTooltip = getIsHealer(specType)
    ? "HPS is the aggregated Healing Per Second score that each Healer spec does in the top rankings of Raid and Mythic+ Dungeons."
    : getIsTank(specType)
    ? "For Tanks we opted to show their aggregated DPS score as a temporary placeholder metric while we search for a more valuable metric that tank players care about."
    : "DPS is the aggregated Damage Per Second score that each DPS spec does in the top rankings of Raid and Mythic+ Dungeons.";

  const tierLetter = builds[encounterType][encounterID][`${dataClassName}-${dataSpecName}`].stats.tier_letter;
  const tierColor = tierListColor(tierLetter);

  const stat_priorities = useMemo(() => {
    const primary = getPrimaryStat(dataClassName, dataSpecName);
    if (primary) {
      return [primary, ...priorities];
    }

    return priorities;
  }, [priorities, primaryStats]);

  return (
    <div className="additional-stats-container">
      <div className="content-section wow-build-stats-normal">
        <div className="basic-slot">
          <div className="tier value" style={{ color: tierColor }}>
            {tierLetter}
          </div>
          <div className="label">
            Tier
            <TooltipContainer tooltipInfo="We calculate spec tier using a combination of their aggregated DPS or HPS score and how often the spec appears in the top rankings of the Raid or Mythic+ dungeons.">
              <div className="tooltip-container">
                <TooltipIcon className="tooltip-icon" />
              </div>
            </TooltipContainer>
          </div>
        </div>
        <div className="divider" />
        <div className="basic-slot">
          <div className="value">{modifiedPickrate}%</div>
          <div className="label">
            Popularity
            <TooltipContainer tooltipInfo="Popularity is how often this spec appears in the top rankings of Raid or Mythic+ Dungeons.">
              <div className="tooltip-container">
                <TooltipIcon className="tooltip-icon" />
              </div>
            </TooltipContainer>
          </div>
        </div>
        <div className="divider" />
        <div className="basic-slot">
          <div className="value">{(statPerSecond / 1000).toFixed(1).toLocaleString("en-US")}K</div>
          <div className="label">
            {statPerSecondLabel}
            <TooltipContainer tooltipInfo={statPerSecondTooltip}>
              <div className="tooltip-container">
                <TooltipIcon className="tooltip-icon" />
              </div>
            </TooltipContainer>
          </div>
        </div>
        <div className="divider" />
        <div className="basic-slot">
          <div className="value">{parses.toLocaleString("en-US")}</div>
          <div className="label">
            Parses
            <TooltipContainer tooltipInfo="Parses is directly tied to Popularity, displayed as the total number of completed encounters by a spec in the top rankings of the Raid.">
              <div className="tooltip-container">
                <TooltipIcon className="tooltip-icon" />
              </div>
            </TooltipContainer>
          </div>
        </div>
      </div>
      <StatPriorities statPriorities={stat_priorities} />
    </div>
  );
};

export const getIcon = (classType, id, color, size, key, spriteData, spritesheet) => {
  if (classType == 3) {
    return (
      <div key={key ?? id} style={{ zIndex: "100" }} spell-id={`${id}`}>
        <OctagonTalentIcon
          iconSize={size}
          color={color}
          staticDataImg={`https://static.bigbrain.gg/wow/spell/assets/${id}.webp`}
          talentSelectedNumber={undefined}
        />
      </div>
    );
  }

  return (
    <div key={key ?? id} style={{ zIndex: "100" }} spell-id={`${id}`}>
      <Icon
        iconSize={size}
        classType={classType == 2 ? "circle" : "square"}
        color={color}
        talentTreeGreyout={false}
        talentSelectedNumber={undefined}
        staticDataImg={spriteData && spritesheet ? spritesheet : `https://static.bigbrain.gg/wow/spell/assets/${id}.webp`}
        spriteData={spriteData}
      />
    </div>
  );
};

export default WOWBuildsContainer;
